import React, { type ReactNode, type SyntheticEvent } from 'react';
import { styled as styled2 } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ButtonProps } from '@atlaskit/button';
import { SpotlightCard, SpotlightPulse } from '@atlaskit/onboarding';
import Portal from '@atlaskit/portal';

import { token } from '@atlaskit/tokens';

import { layers, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';

type Props = {
	title?: string;
	learnMoreLink?: string;
	learnMoreLinkText?: string;
	description: string | ReactNode;
	gotItButtonText: string;
	buttonOverrideProps?: ButtonProps;
	onConfirm: (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

/*
    NOTE: This is not a recommended pattern from the design system or JSM onboarding library
    https://hello.atlassian.net/wiki/spaces/JSMOKIT/pages/1020424544/Common+Components#Spotlight-Dialog-DEPRECATED
*/
export const SpotlightDialogDeprecated = ({
	title,
	onConfirm,
	description,
	learnMoreLink,
	learnMoreLinkText,
	gotItButtonText,
	buttonOverrideProps,
}: Props) => (
	<Portal zIndex={layers.dialog}>
		<SpotlightDialogWrapper>
			<SpotlightCard
				actions={[
					{ text: gotItButtonText, onClick: onConfirm },
					{
						text: learnMoreLinkText,
						...(learnMoreLink && { href: learnMoreLink, target: '_blank' }),
						...buttonOverrideProps,
						appearance: 'subtle-link',
					},
				]}
				width={300}
				heading={title}
			>
				<Body>{description}</Body>
			</SpotlightCard>
		</SpotlightDialogWrapper>
	</Portal>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled2.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 3}px`,
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpotlightDialogWrapper = styled2(SpotlightPulse)({
	borderRadius: '3px',
	position: 'fixed',
	right: token('space.300', '24px'),
	bottom: token('space.300', '24px'),
});
